import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Avatar, ContextMenu, Field, Leaf, Modal, Spinner, ThemeSwitch, type MenuItem } from "../components/ui";
import { api, errorMessage, type SyncRepo, type Project, type ProjectListItem, type TrashedProject } from "../lib/api";
import { Link, navigate, timeAgo, toast, useSession } from "../lib/app";

export function TopNav({ children }: { children?: ReactNode }) {
  const { user, logout } = useSession();
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  if (!user) return null;
  const items: Array<MenuItem | "-"> = [
    { label: "Account settings", onSelect: () => navigate("/account") },
    ...(user.isAdmin ? [{ label: "Admin", onSelect: () => navigate("/admin") }] : []),
    "-",
    { label: "Sign out", onSelect: logout },
  ];
  return (
    <header className="page-top">
      <Link to="/" className="brand">
        <Leaf /> benleaf
      </Link>
      <div className="grow">{children}</div>
      <ThemeSwitch />
      <button
        className="btn btn-ghost"
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setMenu({ x: r.right - 190, y: r.bottom + 4 });
        }}
      >
        <Avatar id={user.id} name={user.displayName} size={24} />
        <span className="hide-narrow">{user.displayName}</span>
      </button>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={items} onClose={() => setMenu(null)} />}
    </header>
  );
}

export function Dashboard() {
  const { user, canManageSync, verification } = useSession();
  // People a non-admin invited can work on shared projects but not start their own until an admin verifies them.
  const canCreate = user?.canCreateProjects !== false;
  const [projects, setProjects] = useState<ProjectListItem[] | null>(null);
  const [filter, setFilter] = useState("");
  const [dialog, setDialog] = useState<null | "blank" | "article" | "upload" | "import">(null);
  const [newMenu, setNewMenu] = useState<{ x: number; y: number } | null>(null);
  const [rowMenu, setRowMenu] = useState<{ x: number; y: number; project: ProjectListItem } | null>(null);
  const [trash, setTrash] = useState<{ projects: TrashedProject[]; days: number } | null>(null);
  // How many projects this user may own; null when there's no limit (admins).
  const [limit, setLimit] = useState<{ max: number; left: number } | null>(null);
  const full = limit?.left === 0;

  const load = () =>
    api
      .get<{ projects: ProjectListItem[]; limit: { max: number; left: number } | null }>("/api/projects")
      .then((r) => {
        setProjects(r.projects);
        setLimit(r.limit);
      })
      .catch((e) => toast(errorMessage(e), "error"));
  const loadTrash = () =>
    api
      .get<{ projects: TrashedProject[]; days: number }>("/api/trash")
      .then(setTrash)
      .catch((e) => toast(errorMessage(e), "error"));
  useEffect(() => {
    load();
    loadTrash();
  }, []);

  const shown = useMemo(
    () => (projects ?? []).filter((p) => p.name.toLowerCase().includes(filter.toLowerCase())),
    [projects, filter],
  );

  async function remove(p: ProjectListItem) {
    const isOwner = p.role === "owner";
    const days = trash?.days ?? 5;
    const msg = isOwner
      ? `Delete "${p.name}"? It moves to your recycle bin, and you can restore it for ${days} ${days === 1 ? "day" : "days"}. Anyone you shared it with loses access until then.`
      : `Leave "${p.name}"?`;
    if (!confirm(msg)) return;
    try {
      if (isOwner) {
        await api.del(`/api/projects/${p.id}`);
        toast(`Moved "${p.name}" to the recycle bin.`);
        loadTrash();
      } else await api.del(`/api/projects/${p.id}/members/${user!.id}`);
      load();
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  }

  async function restore(p: TrashedProject) {
    try {
      await api.post(`/api/trash/${p.id}/restore`);
      toast(`Restored "${p.name}".`, "success");
      load();
      loadTrash();
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  }

  async function deleteForGood(p: TrashedProject) {
    const msg = `Delete "${p.name}" for good now? This can't be undone.${p.linked ? " The copy on the sync server is not deleted." : ""}`;
    if (!confirm(msg)) return;
    try {
      await api.del(`/api/trash/${p.id}`);
      loadTrash();
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  }

  return (
    <div className="page">
      <TopNav />
      <main className="page-main">
        <div className="page-title">
          <h1>Projects</h1>
          <div className="row">
            <input type="search" placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 200 }} />
            {canCreate && limit && (
              <span className={`small ${full ? "chip chip-warn" : "muted"}`} title="Projects you own. Ones shared with you, and ones in your recycle bin, don't count.">
                {limit.max - limit.left} of {limit.max} projects
              </span>
            )}
            {canCreate && (
              <button
                className="btn btn-primary"
                disabled={full}
                title={full ? `You can own up to ${limit!.max} projects. Delete one to make room.` : undefined}
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setNewMenu({ x: r.right - 200, y: r.bottom + 4 });
                }}
              >
                New project ▾
              </button>
            )}
          </div>
        </div>

        {!canCreate && (
          <div className="alert alert-warn" style={{ marginBottom: 16 }}>
            You were invited by {verification?.invitedBy ?? "another user"} rather than an admin, so for now you can only work on projects shared with
            you. To create your own projects, ask {verification?.admins.length ? verification.admins.join(" or ") : "an admin"} to verify your account.
          </div>
        )}

        {projects === null ? (
          <Spinner size={20} />
        ) : projects.length === 0 && !canCreate ? (
          <div className="project-list">
            <div className="empty-state">
              <h3>Nothing shared with you yet</h3>
              <p>Projects people share with you will show up here.</p>
            </div>
          </div>
        ) : projects.length === 0 ? (
          <div className="project-list">
            <div className="empty-state">
              <h3>No projects yet</h3>
              <p>{canManageSync ? "Start from a template, upload a zip from Overleaf, or import a project from the server." : "Start from a template or upload a zip from Overleaf."}</p>
              <div className="row" style={{ justifyContent: "center", marginTop: 16 }}>
                <button className="btn btn-primary" onClick={() => setDialog("upload")}>
                  Upload a zip
                </button>
                <button className="btn" onClick={() => setDialog("blank")}>
                  Blank project
                </button>
                {canManageSync && (
                  <button className="btn" onClick={() => setDialog("import")}>
                    Import from server
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="project-list">
            <div className="project-head">
              <span>Name</span>
              <span>Owner</span>
              <span>Last modified</span>
              <span />
            </div>
            {shown.map((p, i) => (
              <Link key={p.id} to={`/project/${p.id}`} className="project-row">
                <span className="name" style={{ animationDelay: `${i * 25}ms` }}>
                  {p.name}
                  {p.linked && <span className="chip small">synced</span>}
                  {p.syncState.status === "conflict" && <span className="chip chip-bad">conflict</span>}
                </span>
                <span className="muted small">{p.role === "owner" ? "You" : p.ownerName}</span>
                <span className="muted small">{timeAgo(p.updatedAt)}</span>
                <span>
                  <button
                    className="icon-btn"
                    aria-label="Project actions"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setRowMenu({ x: e.clientX - 160, y: e.clientY + 6, project: p });
                    }}
                  >
                    ⋯
                  </button>
                </span>
              </Link>
            ))}
          </div>
        )}

        {!!trash?.projects.length && (
          <section>
            <h2 className="section-title">Recycle bin</h2>
            <p className="muted small" style={{ marginBottom: 12 }}>
              Deleted projects stay here for {trash.days} {trash.days === 1 ? "day" : "days"}, then they're deleted for good. Only you can see your recycle bin.
            </p>
            <div className="project-list">
              <div className="project-head trash-row">
                <span>Name</span>
                <span>Deleted</span>
                <span>Gone for good</span>
                <span />
              </div>
              {trash.projects.map((p) => (
                <div key={p.id} className="project-row trash-row">
                  <span className="name">{p.name}</span>
                  <span className="muted small">{timeAgo(p.deletedAt)}</span>
                  <span className="muted small">
                    <span className="trash-label">Gone for good </span>
                    {purgeLabel(p.purgeAt)}
                  </span>
                  <span className="row">
                    <button className="btn btn-sm" onClick={() => restore(p)}>
                      Restore
                    </button>
                    <button className="btn btn-sm btn-ghost" onClick={() => deleteForGood(p)}>
                      Delete now
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {newMenu && (
        <ContextMenu
          x={newMenu.x}
          y={newMenu.y}
          onClose={() => setNewMenu(null)}
          items={[
            { label: "Blank project", onSelect: () => setDialog("blank") },
            { label: "Article (biblatex) template", onSelect: () => setDialog("article") },
            "-",
            { label: "Upload project (.zip)", onSelect: () => setDialog("upload") },
            ...(canManageSync ? [{ label: "Import from server", onSelect: () => setDialog("import") }] : []),
          ]}
        />
      )}
      {rowMenu && (
        <ContextMenu
          x={rowMenu.x}
          y={rowMenu.y}
          onClose={() => setRowMenu(null)}
          items={[
            { label: "Open", onSelect: () => navigate(`/project/${rowMenu.project.id}`) },
            { label: "Download zip", onSelect: () => (location.href = `/api/projects/${rowMenu.project.id}/download.zip`) },
            "-",
            { label: rowMenu.project.role === "owner" ? "Delete" : "Leave project", danger: true, onSelect: () => remove(rowMenu.project) },
          ]}
        />
      )}
      {(dialog === "blank" || dialog === "article") && <NewProjectDialog template={dialog} onClose={() => setDialog(null)} />}
      {dialog === "upload" && <UploadProjectDialog onClose={() => setDialog(null)} />}
      {dialog === "import" && <ImportRemoteDialog onClose={() => setDialog(null)} />}
    </div>
  );
}

/** When a binned project goes for good: hours on the last day, otherwise the date. The server purges hourly. */
function purgeLabel(ts: number): string {
  const hours = (ts - Date.now()) / 3_600_000;
  if (hours < 1) return "within the hour";
  if (hours < 24) return `in ${Math.floor(hours)} h`;
  return new Date(ts).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

function NewProjectDialog({ template, onClose }: { template: string; onClose: () => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  async function create() {
    setBusy(true);
    try {
      const res = await api.post<{ project: Project }>("/api/projects", { name, template });
      navigate(`/project/${res.project.id}`);
    } catch (e) {
      toast(errorMessage(e), "error");
      setBusy(false);
    }
  }
  return (
    <Modal
      title="New project"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={!name.trim() || busy} onClick={create}>
            {busy ? <Spinner /> : "Create"}
          </button>
        </>
      }
    >
      <Field label="Project name">
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && name.trim() && create()} />
      </Field>
    </Modal>
  );
}

function UploadProjectDialog({ onClose }: { onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  function pick(f: File | undefined) {
    if (!f) return;
    setFile(f);
    if (!name) setName(f.name.replace(/\.zip$/i, ""));
  }
  async function upload() {
    if (!file) return;
    setBusy(true);
    try {
      const res = await api.upload<{ project: Project }>(`/api/projects/upload?name=${encodeURIComponent(name || file.name)}`, file);
      navigate(`/project/${res.project.id}`);
    } catch (e) {
      toast(errorMessage(e), "error");
      setBusy(false);
    }
  }
  return (
    <Modal
      title="Upload project"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={!file || busy} onClick={upload}>
            {busy ? <Spinner /> : "Upload"}
          </button>
        </>
      }
    >
      <div className="stack">
        <div
          className="empty-state"
          style={{
            border: `1.5px dashed var(${drag ? "--accent" : "--line"})`,
            borderRadius: 10,
            padding: 28,
            cursor: "pointer",
            background: drag ? "var(--accent-soft)" : undefined,
          }}
          onClick={() => input.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            pick(e.dataTransfer.files[0]);
          }}
        >
          {file ? (
            <span>
              <b>{file.name}</b> · {(file.size / 1024 / 1024).toFixed(1)} MB
            </span>
          ) : (
            <span>Drop a .zip here (e.g. Overleaf's “Download → Source”), or click to choose.</span>
          )}
          <input ref={input} type="file" accept=".zip,application/zip" hidden onChange={(e) => pick(e.target.files?.[0])} />
        </div>
        <Field label="Project name">
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function ImportRemoteDialog({ onClose }: { onClose: () => void }) {
  const [repos, setRepos] = useState<SyncRepo[] | null>(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<SyncRepo | null>(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .get<{ repos: SyncRepo[] }>("/api/sync/repos")
      .then((r) => setRepos(r.repos))
      .catch((e) => setError(errorMessage(e)));
  }, []);
  async function importRepo() {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await api.post<{ project: Project }>("/api/projects/import-remote", { repo: selected.fullName });
      navigate(`/project/${res.project.id}`);
    } catch (e) {
      toast(errorMessage(e), "error");
      setBusy(false);
    }
  }
  const shown = (repos ?? []).filter((r) => r.fullName.toLowerCase().includes(filter.toLowerCase()));
  return (
    <Modal
      title="Import from server"
      width={560}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={!selected || busy} onClick={importRepo}>
            {busy ? <Spinner /> : "Import & link"}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small" style={{ margin: 0 }}>
          The project stays linked: edits are committed and synced back automatically, and changes on the server are pulled in.
        </p>
        {error && <div className="alert alert-bad">{error}</div>}
        {!repos && !error && <Spinner />}
        {repos && (
          <>
            <input type="search" placeholder="Search repositories…" value={filter} onChange={(e) => setFilter(e.target.value)} autoFocus />
            <div className="repo-list">
              {shown.map((r) => (
                <button key={r.fullName} className={`repo-item${selected?.fullName === r.fullName ? " selected" : ""}`} onClick={() => setSelected(r)}>
                  <span className="grow mono">{r.fullName}</span>
                  {r.private && <span className="chip small">private</span>}
                  {r.empty && <span className="chip small">empty</span>}
                  <span className="muted small">{timeAgo(Date.parse(r.updatedAt))}</span>
                </button>
              ))}
              {shown.length === 0 && <div className="review-empty">No repositories match.</div>}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
